/**
 * Contact forms (plan 16). Sending goes through the sendContactMessage Cloud
 * Function; the saved messages (/contactMessages) are read here, super admin only.
 */
import { useCallback, useEffect, useState } from 'react';
import { collection, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { ContactSource, ContactTopic } from '../config/contactTopics';

export interface ContactInput {
  source: ContactSource;
  topic: ContactTopic;
  message: string;
  name?: string;
  email?: string;
  phone?: string;
  listingId?: string;
  /** Hidden field; people leave it empty. */
  website?: string;
}

export interface ContactMessage {
  id: string;
  source: ContactSource;
  topic: ContactTopic;
  name: string;
  email: string;
  phone: string;
  message: string;
  listingId: string;
  listingNameHe: string;
  listingNameEn: string;
  ownersEmailed: number;
  done: boolean;
  createdAt: string;
}

export function useSendContactMessage() {
  const [sending, setSending] = useState(false);

  /** Throws when it could not be sent (the error code says why). */
  const send = useCallback(async (input: ContactInput) => {
    setSending(true);
    try {
      await httpsCallable<ContactInput, { ok: boolean }>(functions, 'sendContactMessage')(input);
    } finally {
      setSending(false);
    }
  }, []);

  return { sending, send };
}

/** Every contact message, newest first (super admin only). */
export function useContactMessages() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'contactMessages'),
      (snapshot) => {
        setMessages(
          snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() } as ContactMessage))
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        );
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const setDone = useCallback((id: string, done: boolean) => updateDoc(doc(db, 'contactMessages', id), { done }), []);

  return { messages, loading, error, setDone };
}
